import * as React from 'react';

// --- Lib ---
import { parseShortcutKeys } from '../../../../app/rte/lib/tiptap-utils';

// --- Hooks ---
import { useTiptapEditor } from '../../../../app/rte/hooks/use-tiptap-editor';

// --- Tiptap UI ---
import type { UseColorHighlightConfig } from './use-color-highlight';
import {
  COLOR_HIGHLIGHT_SHORTCUT_KEY,
  useColorHighlight,
} from './use-color-highlight';

import { Badge } from '@cotera/client/v0/components/ui/badge';

import { Button } from '@cotera/client/v0/components/ui/button';
import { Tooltip } from '@cotera/client/app/components';

// --- Styles ---

export interface ColorHighlightButtonProps
  extends Omit<React.ComponentProps<'button'>, 'type'>,
    UseColorHighlightConfig {
  /**
   * Optional text to display alongside the icon.
   */
  text?: string;
  /**
   * Optional show shortcut keys in the button.
   * @default false
   */
  showShortcut?: boolean;

  tooltip?: string;
}

export function ColorHighlightShortcutBadge({
  shortcutKeys = COLOR_HIGHLIGHT_SHORTCUT_KEY,
}: {
  shortcutKeys?: string;
}) {
  return <Badge variant="outline">{parseShortcutKeys({ shortcutKeys })}</Badge>;
}

/**
 * Button component for applying color highlights in a Tiptap editor.
 *
 * For custom button implementations, use the `useColorHighlight` hook instead.
 */
export const ColorHighlightButton = React.forwardRef<
  HTMLButtonElement,
  ColorHighlightButtonProps
>(
  (
    {
      editor: providedEditor,
      highlightColor,
      text,
      hideWhenUnavailable = false,
      onApplied,
      showShortcut = false,
      onClick,
      children,
      style,
      ...buttonProps
    },
    ref
  ) => {
    const { editor } = useTiptapEditor(providedEditor);
    const {
      isVisible,
      canColorHighlight,
      isActive,
      handleColorHighlight,
      label,
      shortcutKeys,
    } = useColorHighlight({
      editor,
      highlightColor,
      label: text || `Toggle highlight (${highlightColor})`,
      hideWhenUnavailable,
      onApplied,
    });

    const handleClick = React.useCallback(
      (event: React.MouseEvent<HTMLButtonElement>) => {
        onClick?.(event);
        if (event.defaultPrevented) {
          return;
        }
        handleColorHighlight();
      },
      [handleColorHighlight, onClick]
    );

    const buttonStyle = React.useMemo(
      () =>
        ({
          ...style,
          '--highlight-color': highlightColor,
        } as React.CSSProperties),
      [highlightColor, style]
    );

    if (!isVisible) {
      return null;
    }

    return (
      <Tooltip side="auto" tooltipContent={label}>
        <Button
          type="button"
          variant="ghost"
          data-active-state={isActive ? 'on' : 'off'}
          role="button"
          tabIndex={-1}
          disabled={!canColorHighlight}
          data-disabled={!canColorHighlight}
          aria-label={label}
          aria-pressed={isActive}
          onClick={handleClick}
          style={buttonStyle}
          {...buttonProps}
          ref={ref}
        >
          {children ?? (
            <>
              <span
                className="rounded-full"
                style={{
                  backgroundColor: highlightColor,
                  width: '16px',
                  height: '16px',
                  display: 'inline-block',
                }}
              />
              {text && <span className="text-foreground">{text}</span>}
              {showShortcut && (
                <ColorHighlightShortcutBadge shortcutKeys={shortcutKeys} />
              )}
            </>
          )}
        </Button>
      </Tooltip>
    );
  }
);

ColorHighlightButton.displayName = 'ColorHighlightButton';
